import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Stack, Typography } from '@mui/material';

import { TextField } from '@components/base';
import useChannelsQuery from '@hooks/queryHooks/useChannelsQuery';
import useProvidersQueries from '@hooks/queryHooks/useProvidersQueries';
import { Provider } from '@models/Provider';
import { filterProviders } from '@utils/providerUtils';

type ProviderSelectorProps = {
  includeChannels?: boolean;
  label: string;
  onChange: (providerIdentifier?: string) => void;
  value?: string;
};

const ProviderSelector: FC<ProviderSelectorProps> = ({
  includeChannels,
  label,
  onChange,
  value,
}) => {
  const { t } = useTranslation();

  const { data: { data: channels = [] } = {} } = useChannelsQuery();
  const { data: providers = [] } = useProvidersQueries();

  const filteredProviders = useMemo<Provider[]>(() => filterProviders(providers), [providers]);

  const options = [
    ...filteredProviders.map(({ id, name }) => ({ id, name, type: 'providers' })),
    // If `includeChannels`, add channels to the options list
    ...(includeChannels
      ? channels.map(({ value: { id, name } }) => ({ id, name, type: 'channels' }))
      : []),
  ];

  return (
    <Autocomplete
      getOptionLabel={({ name, id }) => `${name} [${id}]`}
      groupBy={includeChannels ? option => t(option.type) : undefined}
      onChange={(_, val) => onChange(val?.id)}
      options={options}
      renderInput={params => <TextField {...params} label={label} />}
      renderOption={(props, { name, id }) => (
        <li {...props} key={id}>
          <Stack alignItems='flex-start'>
            <Typography variant='inherit'>{name}</Typography>
            <Typography color='text.secondary' variant='caption'>
              [{id}]
            </Typography>
          </Stack>
        </li>
      )}
      value={options.find(({ id }) => id === value) || null}
    />
  );
};

export default ProviderSelector;
